import React, { useState, useEffect } from 'react';
import { getUserDetail, formatDate, formatDateTime, formatTime } from '../userService';
import { getCustomerInfo, grantNorppaPlusEntitlement, parseSubscriptionStatus } from '../revenuecatService';
import UiIcon from './ui/UiIcon';

function UserDetail({ userId, photoURL, onClose }) {
  const highResPhotoURL = photoURL ? photoURL.replace(/=s\d+-c$/, '=s400-c') : null;
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [loadingSubscription, setLoadingSubscription] = useState(true);
  const [grantingEntitlement, setGrantingEntitlement] = useState(false);

  useEffect(() => {
    loadUserDetail();
    loadSubscriptionInfo();
  }, [userId]);

  async function loadUserDetail() {
    setLoading(true);
    try {
      const userData = await getUserDetail(userId);
      setUser(userData);
    } catch (error) {
      console.error('Error loading user detail:', error);
      alert('Failed to load user details');
      onClose();
    } finally {
      setLoading(false);
    }
  }

  async function loadSubscriptionInfo() {
    setLoadingSubscription(true);
    try {
      const customer = await getCustomerInfo(userId);
      const parsed = parseSubscriptionStatus(customer);
      setSubscriptionInfo(parsed);
    } catch (error) {
      console.error('Error loading subscription info:', error);
      setSubscriptionInfo({ status: 'Error loading', active: false, entitlements: [] });
    } finally {
      setLoadingSubscription(false);
    }
  }

  async function handleGrantEntitlement() {
    const durationDays = prompt('How many days should the promotional entitlement last?', '30');
    if (!durationDays) return;

    const duration = parseInt(durationDays, 10);
    if (isNaN(duration) || duration <= 0) {
      alert('Invalid duration');
      return;
    }

    const confirmed = window.confirm(
      `Grant Norppa Plus promotional entitlement to ${user.name} for ${duration} days?`
    );
    if (!confirmed) return;

    setGrantingEntitlement(true);
    try {
      await grantNorppaPlusEntitlement(userId, duration);
      alert(`Successfully granted Norppa Plus for ${duration} days!`);
      // Reload subscription info
      await loadSubscriptionInfo();
    } catch (error) {
      alert(`Failed to grant entitlement: ${error.message}`);
    } finally {
      setGrantingEntitlement(false);
    }
  }

  if (loading) {
    return (
      <div className="user-detail-overlay" onClick={onClose}>
        <div className="user-detail-modal" onClick={(e) => e.stopPropagation()}>
          <p>Loading user details...</p>
        </div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="user-detail-overlay" onClick={onClose}>
      <div className="user-detail-modal" onClick={(e) => e.stopPropagation()}>
        <div className="user-detail-header">
          {highResPhotoURL && (
            <a href={highResPhotoURL.replace(/=s\d+-c$/, '=s800-c')} target="_blank" rel="noreferrer">
              <img className="user-detail-avatar" src={highResPhotoURL} alt="" referrerPolicy="no-referrer" />
            </a>
          )}
          <h2>{user.name}</h2>
          <button className="close-button" onClick={onClose} aria-label="Close"><UiIcon name="close" /></button>
        </div>

        <div className="user-detail-content">
          <section className="user-detail-section">
            <h3>Basic Info</h3>
            <div className="user-detail-grid">
              <div className="user-detail-item">
                <span className="label">Email:</span>
                <span className="value">{user.email}</span>
              </div>
              <div className="user-detail-item">
                <span className="label">Signup Date:</span>
                <span className="value">{formatDate(user.createdAt)}</span>
              </div>
              <div className="user-detail-item">
                <span className="label">Last Active:</span>
                <span className="value">{formatDate(user.lastActive)}</span>
              </div>
            </div>
          </section>

          <section className="user-detail-section">
            <h3>Subscription (RevenueCat)</h3>
            {loadingSubscription ? (
              <p className="no-data">Loading subscription info...</p>
            ) : (
              <>
                <div className="user-detail-grid">
                  <div className="user-detail-item">
                    <span className="label">Status:</span>
                    <span className={`value subscription-status ${subscriptionInfo.active ? 'active' : 'inactive'}`}>
                      {subscriptionInfo.status}
                    </span>
                  </div>
                  {subscriptionInfo.expirationDate && (
                    <div className="user-detail-item">
                      <span className="label">Expires:</span>
                      <span className="value">{subscriptionInfo.expirationDate.toLocaleDateString()}</span>
                    </div>
                  )}
                  {subscriptionInfo.entitlements.length > 0 && (
                    <div className="user-detail-item">
                      <span className="label">Entitlements:</span>
                      <span className="value">{subscriptionInfo.entitlements.join(', ')}</span>
                    </div>
                  )}
                </div>
                <button
                  className="btn-primary grant-entitlement-button"
                  onClick={handleGrantEntitlement}
                  disabled={grantingEntitlement}
                >
                  {grantingEntitlement ? 'Granting...' : 'Grant Norppa Plus'}
                </button>
              </>
            )}
          </section>

          <section className="user-detail-section">
            <h3>Crossword Stats</h3>
            <div className="user-detail-grid">
              <div className="user-detail-item">
                <span className="label">Current Streak:</span>
                <span className="value">{user.streakCount} days</span>
              </div>
              <div className="user-detail-item">
                <span className="label">Streak Freezes:</span>
                <span className="value">{user.streakFreezes}</span>
              </div>
              <div className="user-detail-item">
                <span className="label">Total Completed:</span>
                <span className="value">{user.crosswordStats.completedCount}</span>
              </div>
            </div>
          </section>

          <section className="user-detail-section">
            <h3>Wordle Stats</h3>
            <div className="user-detail-grid">
              <div className="user-detail-item">
                <span className="label">Games Played:</span>
                <span className="value">{user.wordleStats.gamesPlayed}</span>
              </div>
              <div className="user-detail-item">
                <span className="label">Games Won:</span>
                <span className="value">{user.wordleStats.gamesWon}</span>
              </div>
              <div className="user-detail-item">
                <span className="label">Win Rate:</span>
                <span className="value">
                  {user.wordleStats.gamesPlayed > 0
                    ? `${Math.round((user.wordleStats.gamesWon / user.wordleStats.gamesPlayed) * 100)}%`
                    : 'N/A'}
                </span>
              </div>
            </div>

            {Object.keys(user.wordleStats.guessDistribution).length > 0 && (
              <div className="guess-distribution">
                <h4>Guess Distribution</h4>
                <div className="distribution-bars">
                  {[1, 2, 3, 4, 5, 6].map(num => {
                    const count = user.wordleStats.guessDistribution[num] || 0;
                    const maxCount = Math.max(...Object.values(user.wordleStats.guessDistribution), 1);
                    const percentage = (count / maxCount) * 100;

                    return (
                      <div key={num} className="distribution-row">
                        <span className="guess-number">{num}</span>
                        <div className="distribution-bar-container">
                          <div
                            className="distribution-bar"
                            style={{ width: `${percentage}%` }}
                          >
                            {count > 0 && <span className="count-label">{count}</span>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </section>

          <section className="user-detail-section">
            <h3>Recent Crosswords</h3>
            {user.recentCrosswords.length > 0 ? (
              <div className="recent-activity-list">
                {user.recentCrosswords.map(puzzle => (
                  <div key={puzzle.id} className="activity-item">
                    <span className="activity-date">{puzzle.id}</span>
                    <span className="activity-time">{formatTime(puzzle.completionTime)}</span>
                    <span className="activity-timestamp">{formatDateTime(puzzle.completedAt)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="no-data">No completed crosswords</p>
            )}
          </section>

          <section className="user-detail-section">
            <h3>Recent Wordle</h3>
            {user.recentWordle.length > 0 ? (
              <div className="recent-activity-list">
                {user.recentWordle.map(game => (
                  <div key={game.id} className="activity-item">
                    <span className="activity-date">{game.id}</span>
                    <span className={`wordle-result ${game.isWon ? 'won' : 'lost'}`}>
                      {game.isWon ? `${game.guessCount}/6` : 'X/6'}
                    </span>
                    <span className="activity-timestamp">{formatDateTime(game.lastUpdated)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="no-data">No wordle games played</p>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

export default UserDetail;
